import { disposeMesh } from 'src/utils/object3DHelpers';
import { BarrierDirection, ComponentInteractionType, Inputs, SceneComponent } from './types';

const WIDTH = 1;
const HEIGHT = 1;
const DEPTH = 1;
const DEFAULT_INPUTS = {
  colorDefault: '#ea475c',
  colorHover: '#ffffff',
  colorClick: '#ea475c',
  opacityDefault: 0.2,
  opacityHover: 0.4,
  opacityClick: 0.5,
  edgesOpacityDefault: 0.5,
  edgesOpacityHover: 0.7,
  edgesOpacityClick: 0.8,
  direction: 'none',
  adminPanelAccess: false,
};

export class BarrierComponent extends SceneComponent {
  private root: any = null;

  private pivot: any = null;

  private barrier: any = null;

  private frontSide: any = null;

  private backSide: any = null;

  private edges: any = null;

  private currentOpacity = 0.2;

  private direction = 'none';

  private clicked = false;

  inputs: Inputs = {
    ...DEFAULT_INPUTS,
  };

  events = {
    [ComponentInteractionType.CLICK]: true,
    [ComponentInteractionType.HOVER]: true,
    [ComponentInteractionType.DRAG]: false,
  };

  onInit() {
    const THREE = this.context.three;

    this.root = new THREE.Object3D();
    this.pivot = new THREE.Object3D();
    // setting the collider to add the click event
    if (this.inputs.adminPanelAccess) this.outputs.collider = this.root;
    this.outputs.objectRoot = this.root;
    this.root.add(this.pivot);
    this.currentOpacity = this.inputs.opacityDefault;
    this.direction = this.inputs.direction;

    this.makeBarrier();
  }

  onDestroy() {
    if (this.barrier) {
      disposeMesh(this.barrier);
      this.barrier = null;
    }
    if (this.edges) {
      disposeMesh(this.edges);
      this.edges = null;
    }
  }

  // Called back when any inputs properties change like localPosition or localRotation
  onInputsUpdated() {
    this.onInit();
  }

  // used to fire it from outside event handler,
  // as internal handler doesn't work if outside handler is defined
  handleHover(event: any) {
    if (!this.clicked) {
      if (event.hover) {
        this.currentOpacity = this.inputs.opacityHover;
      } else {
        this.currentOpacity = this.inputs.opacityDefault;
      }
    }
  }

  handleClick() {
    if (!this.clicked) {
      this.currentOpacity = this.inputs.opacityClick;
      this.clicked = true;
    } else {
      this.currentOpacity = this.inputs.opacityDefault;
      this.clicked = false;
    }
    setTimeout(() => {
      this.clicked = false;
    }, 1000);
  }

  setSelection(color: string) {
    this.inputs.colorDefault = color || this.inputs.colorClick;
    this.inputs.opacityDefault = color ? this.inputs.opacityDefault : this.inputs.opacityClick;
    if (this.barrier) {
      (this.barrier.material as any).color.setStyle(this.inputs.colorDefault);
    }
    if (this.edges) {
      (this.edges.material as any).opacity = this.inputs.opacityDefault;
    }
  }

  setDirection(direction: string) {
    this.direction = direction;
    if (this.frontSide) {
      if (direction === BarrierDirection.Forward) this.pivot.rotation.y = 0;
      if (direction === BarrierDirection.Backward) this.pivot.rotation.y = Math.PI;
      this.frontSide.visible = direction !== BarrierDirection.None;
      this.backSide.scale.z = direction !== BarrierDirection.None ? DEPTH / 2 : DEPTH;
      this.backSide.position.z = this.direction !== BarrierDirection.None ? -DEPTH / 4 : 0;
    }
  }

  resetToDefaultColor() {
    this.inputs.colorDefault = DEFAULT_INPUTS.colorClick;
    this.inputs.opacityClick = DEFAULT_INPUTS.opacityClick;
    (this.barrier.material as any).color.setStyle(this.inputs.colorDefault);
    (this.edges.material as any).opacity = this.inputs.opacityDefault;
  }

  hide() {
    (this.barrier.material as any).opacity = 0;
    (this.edges.material as any).opacity = 0;
  }

  setVisible(visible: boolean) {
    this.frontSide.material.opacity = visible ? this.currentOpacity : 0;
    this.backSide.material.opacity = visible ? this.currentOpacity : 0;
  }

  private makeBarrier() {
    const THREE = this.context.three;

    if (this.barrier) {
      disposeMesh(this.barrier);
      this.barrier = null;
    }
    if (this.edges) {
      disposeMesh(this.edges);
      this.edges = null;
    }

    const boxGeometry: any = new THREE.BoxGeometry(WIDTH, HEIGHT, DEPTH);

    const frontMaterial: any = new THREE.MeshBasicMaterial({
      transparent: true,
      opacity: this.currentOpacity,
      side: THREE.DoubleSide,
      // blending: THREE.AdditiveBlending,
      blending: THREE.NormalBlending,
      depthWrite: false,
      polygonOffset: true,
      polygonOffsetFactor: -5,
    });
    const backMaterial = new THREE.MeshBasicMaterial().copy(frontMaterial);

    frontMaterial.color.setStyle('green');
    backMaterial.color.setStyle('red');

    this.frontSide = new THREE.Mesh(boxGeometry, frontMaterial);
    this.frontSide.name = 'barrierFront';
    this.backSide = new THREE.Mesh(boxGeometry, backMaterial);
    this.backSide.name = 'barrierBack';

    this.barrier = new THREE.Group();
    this.barrier.add(this.frontSide);
    this.barrier.add(this.backSide);

    this.frontSide.position.z = DEPTH / 4;
    this.frontSide.scale.z = DEPTH / 2;

    this.setDirection(this.direction);
    this.pivot?.add(this.barrier);
  }
}

export const barrierComponentType = 'treedis.barrierComponent';
export const makeBarrierComponent = () => new BarrierComponent();
